import { useMemo } from "react";
import { useSelector } from "react-redux";
import type { RateBasedHotelRoomCardRatesProps } from "@b2bportal/core-hotels";
import { useI18nContext } from "@hopper-b2b/i18n";
import { useGetPriceDisplay } from "@tenant/ui";
import { useBaseRateSelection } from "../../hotels/shop/components/HotelRoomCardRates/useBaseRateSelection";
import { useMealPlanRatesForBaseRate } from "../../hotels/shop/components/HotelRoomCardRates/useMealPlanRatesForBaseRate";
import { AccountSelectors } from "@/modules/account";
import { PackageHotelShopSelectors } from "@/store/packages";
import { adjustIncrementalPriceForDisplay } from "@/utilities/prices";

export const usePackageHotelRoomCardRates = ({
  rates,
  selectedRate,
  setSelectedRate,
}: Omit<RateBasedHotelRoomCardRatesProps, "nightCount">) => {
  const { t } = useI18nContext();
  const { getPriceDisplay } = useGetPriceDisplay();

  const rewardsAccountId = useSelector(AccountSelectors.getRewardsAccountId);
  const packagesByRateId = useSelector(
    PackageHotelShopSelectors.getPackagesByRateId
  );

  // Get package price for the selected rate
  const selectedRateId = selectedRate.product.rateId?.value;
  const packagePrice = selectedRateId
    ? packagesByRateId?.[selectedRateId]?.price
    : undefined;

  const { priceText, rewardsText, showPointsPricing } = useMemo(() => {
    if (!packagePrice?.incrementalChange) {
      // Fallback to hotel-only pricing if package price not available
      const selectedProduct = selectedRate.product;

      const accountSpecificPrice =
        selectedProduct.tripTotal.accountSpecific?.[rewardsAccountId];

      const showPoints =
        rewardsAccountId != null &&
        accountSpecificPrice != null &&
        Object.keys(accountSpecificPrice).length > 0;

      const { priceText, rewardsText } = getPriceDisplay({
        prices: selectedProduct.tripTotal,
      });

      return {
        priceText,
        rewardsText,
        showPointsPricing: showPoints,
      };
    }

    // Use package pricing
    const accountSpecificPrice =
      packagePrice.incrementalChange.accountSpecific?.[rewardsAccountId];

    const showPoints =
      rewardsAccountId != null &&
      accountSpecificPrice != null &&
      Object.keys(accountSpecificPrice).length > 0;

    const { priceText, rewardsText } = getPriceDisplay({
      prices: adjustIncrementalPriceForDisplay(packagePrice.incrementalChange),
      formatOptions: { signDisplay: "always" },
    });

    return {
      priceText,
      rewardsText,
      showPointsPricing: showPoints,
    };
  }, [packagePrice, selectedRate, rewardsAccountId, getPriceDisplay]);

  const { baseRates, selectedBaseRate, onSelectBaseRateId } =
    useBaseRateSelection({ rates, selectedRate, setSelectedRate });

  const { mealPlanRatesForBaseRate, showMealPlans } =
    useMealPlanRatesForBaseRate({
      rates,
      baseRate: selectedBaseRate,
    });

  return useMemo(
    () => ({
      context: {
        rates,
        selectedRate,
        selectedBaseRate,
        baseRates,
        showPointsPricing,
        showMealPlans,
        text: {
          priceText,
          rewardsText,
        },
      },
      handlers: {
        onSelectBaseRateId,
        mealPlanRatesForBaseRate,
      },
    }),
    [
      rates,
      selectedRate,
      selectedBaseRate,
      baseRates,
      showPointsPricing,
      showMealPlans,
      priceText,
      rewardsText,
      onSelectBaseRateId,
      mealPlanRatesForBaseRate,
    ]
  );
};
